import { baseApi, PAYMENT_METHODS_TAG } from '@/shared';
import {
  paymentMethodsAdapter,
  paymentMethodsGetImagesAdapter,
} from '../utils/helpers/adapter';

import type {
  TPaymentMethod,
  TPaymentMethodData,
  TPaymentMethodDataServer,
  TPaymentMethodCreateParams,
  TPaymentMethodPartialUpdateParams,
  TPaymentMethodGetImagesParams,
  TPaymentMethodUploadImageParams,
  TPaymentMethodCreateCredentialsParams,
  TPaymentMethodUpdateCredentialsParams,
  TPaymentMethodDeleteCredentialsParams,
} from './types';

const getMethodData = (data: TPaymentMethodData | undefined): (TPaymentMethodDataServer | undefined) => {
  return data
    ? {
      blockchain: data.blockchain ? {
        network: data.blockchain.network,
        enabled: data.blockchain.enabled,
        address_field: data.blockchain.addressField,
        required_confirmations: data.blockchain.requiredConfirmations,
      } : undefined,
      step1: {
        title: data.step1.title,
        options: data.step1.options,
      },
      step2: {
        title: data.step2.title,
        main_text: data.step2.mainText,
      },
      step3: {
        title: data.step3.title,
      },
      confirmation: {
        type: data.confirmation.type,
        file_text: data.confirmation.fileText,
        additional_text: data.confirmation.additionalText,
        phone_country: data.confirmation.phoneCountry,
      },
    }
    : undefined;
}

export const paymentMethodsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getPaymentMethods: build.query<TPaymentMethod[], void>({
      query: () => ({
        url: '/admin/payment_methods/list',
      }),
      transformResponse: paymentMethodsAdapter,
      providesTags: [PAYMENT_METHODS_TAG],
    }),

    createPaymentMethod: build.mutation<void, TPaymentMethodCreateParams>({
      query: (params: TPaymentMethodCreateParams) => ({
        url: '/admin/payment_methods/create',
        method: 'POST',
        body: {
          key: params.key,
          name: params.name,
          min_refill: params.minRefill,
          max_refill: params.maxRefill,
          refill_percent: params.refillPercent,
          additional_percent: params.additionalPercent,
          payment_expire_minutes: params.paymentExpireMinutes,
          currency: params.currency,
          active: params.active,
          use_unique_amount: params.useUniqueAmount,
          unique_amount_step: params.uniqueAmountStep,
          mode: params.mode,
          remote_method_id: params.remoteMethodId,
          conversion_pair_id: params.conversionPairId,
          data: getMethodData(params.data),
        },
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    updatePaymentMethod: build.mutation<void, TPaymentMethodPartialUpdateParams>({
      query: ({ methodId, ...params }: TPaymentMethodPartialUpdateParams) => ({
        url: `/admin/payment_methods/update/${methodId}`,
        method: 'PUT',
        body: {
          name: params.name,
          min_refill: params.minRefill,
          max_refill: params.maxRefill,
          refill_percent: params.refillPercent,
          additional_percent: params.additionalPercent,
          payment_expire_minutes: params.paymentExpireMinutes,
          active: params.active,
          use_unique_amount: params.useUniqueAmount,
          unique_amount_step: params.uniqueAmountStep,
          mode: params.mode,
          remote_method_id: params.remoteMethodId,
          conversion_pair_id: params.conversionPairId,
          data: getMethodData(params.data),
        },
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    deletePaymentMethod: build.mutation<void, { methodId: number }>({
      query: ({ methodId }) => ({
        url: `/admin/payment_methods/delete/${methodId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    getPaymentMethodImages: build.query<string[], TPaymentMethodGetImagesParams>({
      query: (params) => ({
        url: '/admin/images/list',
        method: 'GET',
        params: {
          page_size: params.pageSize,
          page: params.page,
          ascending: params.ascending,
        }
      }),
      transformResponse: paymentMethodsGetImagesAdapter,
      providesTags: [PAYMENT_METHODS_TAG],
    }),

    uploadPaymentMethodImage: build.mutation<void, TPaymentMethodUploadImageParams>({
      query: (params: TPaymentMethodUploadImageParams) => ({
        url: '/admin/images/upload',
        method: 'POST',
        body: params.body,
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    createPaymentMethodCredentials: build.mutation<void, TPaymentMethodCreateCredentialsParams>({
      query: (params: TPaymentMethodCreateCredentialsParams) => ({
        url: `/admin/payment_methods/add_credentials/${params.methodId}`,
        method: 'POST',
        body: {
          fields: params.fields,
          valid_for: params.validFor,
          limit: params.limit,
          shop_id: params.shopId,
          except_payer_id: params.exceptPayerId,
          for_payer_id: params.forPayerId,
        },
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    updatePaymentMethodCredentials: build.mutation<void, TPaymentMethodUpdateCredentialsParams>({
      query: (params: TPaymentMethodUpdateCredentialsParams) => ({
        url: `/admin/payment_methods/update_credentials/${params.credentialsId}`,
        method: 'PUT',
        body: {
          fields: params.fields,
          valid_for: params.validFor,
          limit: params.limit,
          shop_id: params.shopId,
          except_payer_id: params.exceptPayerId,
          for_payer_id: params.forPayerId,
        },
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

    deletePaymentMethodCredentials: build.mutation<void, TPaymentMethodDeleteCredentialsParams>({
      query: (params: TPaymentMethodDeleteCredentialsParams) => ({
        url: `/admin/payment_methods/delete_credentials/${params.credentialsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [PAYMENT_METHODS_TAG],
    }),

  }),
});

export const {
  useGetPaymentMethodsQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useGetPaymentMethodImagesQuery,
  useUploadPaymentMethodImageMutation,
  useCreatePaymentMethodCredentialsMutation,
  useUpdatePaymentMethodCredentialsMutation,
  useDeletePaymentMethodCredentialsMutation,
} = paymentMethodsApi;
